import {
  PF_SET_TOTE_SET,
  PF_SET_INPUT,
  PF_SET_STAGE,
  PF_SET_SCAN_RESULT,
  PF_CONFIRM_TOTE,
  PF_SET_ORDER_COMPLETE,
  PF_SET_SCAN_SUCCESS,
  PF_SET_SCAN_ERROR,
  PF_SET_LOADING,
  PF_SET_NOTICE,
  PF_SET_PROMPT,
  PF_SET_FOCUS,
  PF_SET_POSTSCAN,
  PF_RESET
} from '../constants';
import i18n from 'i18n-js';
import {Platform, Linking} from 'react-native';
import audioService from '../services/AudioService';
import {navigate} from '../components/root_navigation';
import {
  productFirstScan,
  scanToTote,
  assignUserToteSet,
  addOrderNote,
  clearOrderTote,
  removeItemFromOrder,
  fetchShipment
} from '../services/productFirstApiService';
import {
  buildPostScanOrder,
  needsShipmentFetch,
  getCompletionPopupUrl
} from '../components/putwall/utils/postScanPopups';
import {selectToteIdentifier} from '../selectors/productFirstSelectors';
import {getPostScanSteps} from '../utils/postScanning/postScanConfig';
import {runPostScanFlow} from '../utils/postScanning/postScanRunner';
import {createPostScanDriver} from '../utils/postScanning/createPostScanDriver';
import {putWallPostScanConfig} from '../components/putwall/utils/putWallPostScanConfig';
import {
  mapProductScanResult,
  applyToteConfirmation,
  buildToteScanPayload,
  isCorrectTote,
  isOrderCompleteResponse,
  getNoticeMessage
} from '../components/putwall/utils/productFirstMappers';

export const setInput = value => ({type: PF_SET_INPUT, payload: value});
export const setStage = stage => ({type: PF_SET_STAGE, payload: stage});
export const setFocus = hasFocus => ({type: PF_SET_FOCUS, payload: hasFocus});
export const setToteSet = toteSet => ({type: PF_SET_TOTE_SET, payload: toteSet});
export const resetProductFirst = () => ({type: PF_RESET});
export const clearNotice = () => ({type: PF_SET_NOTICE, payload: ''});

export function setPrompt(isVisible, message = '', type = '') {
  return {type: PF_SET_PROMPT, payload: {isVisible, message, type}};
}

function flash(successType) {
  return async dispatch => {
    const type = successType ? PF_SET_SCAN_SUCCESS : PF_SET_SCAN_ERROR;
    dispatch(setFocus(false));
    dispatch({type, payload: true});
    await (successType
      ? audioService.playSuccessSound()
      : audioService.playErrorSound());
    dispatch(setInput(''));
    dispatch(setFocus(true));
    setTimeout(() => dispatch({type, payload: false}), 120);
  };
}

export function assignToteSet(input) {
  return async (dispatch, getState) => {
    if (!input) return;
    dispatch({type: PF_SET_LOADING, payload: true});
    try {
      const res = await assignUserToteSet(input);
      if (res?.status) {
        dispatch(setToteSet(res.tote_set));
        await dispatch(flash(true));
      } else {
        const toteIdentifier = selectToteIdentifier(getState());
        dispatch({
          type: PF_SET_NOTICE,
          payload: i18n.t('productFirst.invalidTote', {toteIdentifier})
        });
        await dispatch(flash(false));
      }
    } catch (e) {
      await dispatch(flash(false));
    } finally {
      dispatch({type: PF_SET_LOADING, payload: false});
    }
  };
}

function openExternalUrl(url) {
  if (Platform.OS === 'web') {
    window.open(url, '', `width=${screen.availWidth},height=${screen.availHeight}`);
  } else {
    Linking.openURL(url);
  }
}

function runCompletionPopups(order) {
  return async (dispatch, getState) => {
    if (!order) return;
    let resolved = order;
    if (needsShipmentFetch(order)) {
      try {
        const data = await fetchShipment(order.store_id, order.id);
        resolved = {...order, shipment_id: data?.shipment_id};
      } catch (e) {}
    }
    const generalSettings =
      getState().user.bothSettings?.data?.data?.general_setting;
    const url = getCompletionPopupUrl(resolved, generalSettings);
    if (url) openExternalUrl(url);
  };
}

export function handleCompletionPopups(res) {
  return runCompletionPopups(buildPostScanOrder(res));
}

function scheduleCompletionReset(seconds) {
  return (dispatch, getState) => {
    const ms = (seconds || 4) * 1000;
    setTimeout(() => {
      if (getState().productFirst.orderComplete) {
        dispatch(resetProductFirst());
      }
    }, ms);
  };
}

function finalizePutWallCompletion() {
  return async (dispatch, getState) => {
    const {popupOrder, resetSeconds} = getState().productFirst.postScan || {};
    dispatch({type: PF_SET_POSTSCAN, payload: {active: false}});
    await dispatch(runCompletionPopups(popupOrder));
    dispatch(scheduleCompletionReset(resetSeconds));
  };
}

const putWallDriver = createPostScanDriver({
  ...putWallPostScanConfig,
  finalize: finalizePutWallCompletion
});

export function handleOrderCompletion(res, resetSeconds) {
  return async (dispatch, getState) => {
    dispatch({
      type: PF_SET_POSTSCAN,
      payload: {
        active: true,
        popupOrder: buildPostScanOrder(res),
        resetSeconds
      }
    });
    // A single-item order is already completed server-side by the product scan,
    // and the legacy client ran no post-scan steps for it — just the completion
    // message.
    const {steps} = res?.single_item_order ? {steps: []} : getPostScanSteps(getState());
    const order = res.order;
    await dispatch(
      runPostScanFlow(putWallDriver, {
        order,
        orderId: order?.id,
        modules: steps,
        toteInput: null
      })
    );
  };
}

export function resumePostScan(remainingModules = []) {
  return async (dispatch, getState) => {
    const {popupOrder} = getState().productFirst.postScan || {};
    await dispatch(
      runPostScanFlow(putWallDriver, {
        order: popupOrder,
        orderId: popupOrder?.id,
        modules: remainingModules,
        toteInput: null
      })
    );
  };
}

export function scanProduct(input) {
  return async dispatch => {
    if (!input) return;
    dispatch({type: PF_SET_LOADING, payload: true});
    try {
      const res = await productFirstScan({input, app: 'app'});
      const mapped = mapProductScanResult(res);
      if (mapped.outcome === 'notice') {
        dispatch({
          type: PF_SET_NOTICE,
          payload: mapped.notice || i18n.t('productFirst.scanFailed')
        });
        await dispatch(flash(false));
        return;
      }
      dispatch({type: PF_SET_SCAN_RESULT, payload: mapped});
      await dispatch(flash(true));
      if (mapped.orderComplete) {
        dispatch(handleOrderCompletion(res, mapped.completeResetSeconds));
      }
    } catch (e) {
      await dispatch(flash(false));
    } finally {
      dispatch({type: PF_SET_LOADING, payload: false});
    }
  };
}

export function scanTote(input) {
  return async (dispatch, getState) => {
    const state = getState().productFirst;
    const {pendingScan, suggestedItem, itemsInTote, unscannedItems} = state;
    if (!pendingScan || !input) return;

    if (!isCorrectTote(input, pendingScan.tote)) {
      const toteIdentifier = selectToteIdentifier(getState());
      dispatch({
        type: PF_SET_NOTICE,
        payload: i18n.t('productFirst.wrongTote', {toteIdentifier})
      });
      await dispatch(flash(false));
      return;
    }

    const completing = pendingScan.type === 'scan_tote_to_complete';
    const confirmed = applyToteConfirmation(suggestedItem, itemsInTote, unscannedItems);

    if (completing) {
      dispatch({
        type: PF_SET_ORDER_COMPLETE,
        payload: {
          orderComplete: true,
          completeMessage: state.completeMessage,
          itemsInTote: confirmed.itemsInTote,
          unscannedItems: []
        }
      });
    } else {
      dispatch({type: PF_CONFIRM_TOTE, payload: confirmed});
    }
    await dispatch(flash(true));

    const snapshot = {
      order: state.order,
      tote: state.tote,
      outcome: state.outcome,
      bannerState: state.bannerState,
      suggestedItem,
      itemsInTote,
      unscannedItems,
      pendingScan,
      orderComplete: false,
      stage: 'tote'
    };

    try {
      const res = await scanToTote(buildToteScanPayload(pendingScan, input));
      if (res?.status === false && !isOrderCompleteResponse(res)) {
        dispatch({type: PF_SET_SCAN_RESULT, payload: snapshot});
        dispatch({
          type: PF_SET_NOTICE,
          payload: getNoticeMessage(res) || i18n.t('productFirst.scanFailed')
        });
        await dispatch(flash(false));
        return;
      }
      if (isOrderCompleteResponse(res)) {
        dispatch({
          type: PF_SET_ORDER_COMPLETE,
          payload: {
            orderComplete: true,
            completeMessage: res.multi_item_order_message || '',
            itemsInTote: res.order_items_scanned || confirmed.itemsInTote,
            unscannedItems: []
          }
        });
        dispatch(handleOrderCompletion(res, res.multi_item_order_message_time));
      }
    } catch (e) {
      dispatch({type: PF_SET_SCAN_RESULT, payload: snapshot});
      await dispatch(flash(false));
    }
  };
}

export function handleInput() {
  return (dispatch, getState) => {
    const {stage, input} = getState().productFirst;
    if (!input) return;
    dispatch(stage === 'tote' ? scanTote(input) : scanProduct(input));
  };
}

export function redirectToProductDetail(productId) {
  return (dispatch, getState) => {
    const {order} = getState().productFirst;
    const userData = getState().user;
    navigate('ProductDetail', {
      productInfo: productId,
      orderID: order?.id,
      increment_id: order?.increment_id,
      userData,
      page: 'PutWall'
    });
  };
}

export const openAction = type => setPrompt(true, '', type);
export const closeAction = () => setPrompt(false, '', '');

export function passItem() {
  return (dispatch, getState) => {
    const {itemsInTote, unscannedItems} = getState().productFirst;
    dispatch({
      type: PF_CONFIRM_TOTE,
      payload: {itemsInTote, unscannedItems, suggestedItem: null}
    });
  };
}

export function submitAddNote(note) {
  return async (dispatch, getState) => {
    const {order} = getState().productFirst;
    dispatch(closeAction());
    if (!order || !note) return;
    dispatch({type: PF_SET_LOADING, payload: true});
    try {
      const res = await addOrderNote(order.id, note);
      if (res?.status) {
        await dispatch(flash(true));
      } else {
        dispatch({type: PF_SET_NOTICE, payload: getNoticeMessage(res)});
        await dispatch(flash(false));
      }
    } catch (e) {
      await dispatch(flash(false));
    } finally {
      dispatch({type: PF_SET_LOADING, payload: false});
    }
  };
}

export function restartOrder() {
  return async (dispatch, getState) => {
    const {order} = getState().productFirst;
    dispatch(closeAction());
    if (!order) return;
    dispatch({type: PF_SET_LOADING, payload: true});
    try {
      await clearOrderTote(order.id);
      dispatch(resetProductFirst());
      await dispatch(flash(true));
    } catch (e) {
      await dispatch(flash(false));
    } finally {
      dispatch({type: PF_SET_LOADING, payload: false});
    }
  };
}

export function removeCurrentItem() {
  return async (dispatch, getState) => {
    const {suggestedItem} = getState().productFirst;
    dispatch(closeAction());
    if (!suggestedItem) return;
    dispatch({type: PF_SET_LOADING, payload: true});
    try {
      const res = await removeItemFromOrder(suggestedItem.order_item_id);
      if (res?.status === false) {
        dispatch({type: PF_SET_NOTICE, payload: getNoticeMessage(res)});
        await dispatch(flash(false));
      } else {
        dispatch(resetProductFirst());
        await dispatch(flash(true));
      }
    } catch (e) {
      await dispatch(flash(false));
    } finally {
      dispatch({type: PF_SET_LOADING, payload: false});
    }
  };
}
